/**
 * @file Single source of truth for the `allEntityIdsAndGroupIds` array stored on
 * `FormVersion` and `EntityRecord` documents: the de-duplicated set of entity ids and
 * group ids the form-views query filters by (`array-contains`). Consumers:
 * `useSaveEntityReference` and `useEntityRecordUI` on the client, and
 * `restoreFormVersionTo` on the server (SYD-2450). It lives in `types/src` — beside
 * `promotableVersionId` — because client and server must not be able to disagree about
 * which ids a reference map yields: a second copy of this rule would let a server write
 * drop a form out of a view the client had put it in.
 *
 * The map key in `EntityReferences` is polymorphic — `entityId` for a direct reference,
 * but `entityGroupId` for a grouped one — so the key alone cannot yield the underlying
 * entity id. A grouped reference written by current code carries its real `entityId`, but
 * references persisted before that field existed do not. For those, `resolveEntityId`
 * (built from the form layout via `entityIdByReferenceKey`) recovers the entity id from
 * the map key, so a save never collapses a legacy grouped reference onto its group id and
 * drops its entity from the filter index. The bare `key` is the last-resort fallback for a
 * reference absent from the layout. A caller with no layout in hand — the server — must
 * refuse rather than rely on that fallback for a grouped reference; see
 * `restoreFormVersionTo`.
 *
 * Writers remove a cleared reference from the map outright, but a `null` `entityRecordId`
 * is still skipped defensively so a cleared reference can never leave its entity behind in
 * the filter index.
 */
import type { EntityReferences } from './System.types';

export const entityReferenceIndex = (
    references: EntityReferences,
    resolveEntityId?: (referenceKey: string) => string | undefined,
): string[] => {
    const ids = new Set<string>();
    Object.entries(references).forEach(([key, ref]) => {
        if (!ref.entityRecordId) return;
        ids.add(ref.entityId ?? resolveEntityId?.(key) ?? key);
        if (ref.entityGroupId) ids.add(ref.entityGroupId);
    });
    return Array.from(ids);
};
